import { useEffect, useRef } from 'react';
import {
  FT_INNER,
  FT_MIDDLE,
  FT_OUTER,
  DEFAULT_ARROW_LENGTH_FT,
  METERS_PER_FOOT,
  MIN_DRAG_FT,
  computeBearingDeg,
  computeDistanceFeet,
} from './isdBlockGeometry';
/* Fit-to-circle hit radius in feet (matches FT_OUTER). Keep a local alias so a
   future change to the outer radius only needs the import above. */
const HIT_RADIUS_FT = FT_OUTER;

const COLOR_ACTIVE = '#FFEE00'; // bright safety yellow
const COLOR_ACTIVE_HALO = '#000000';

/**
 * Handles interactive drawing/editing for the ISD Point tool. Active only when
 * `pathsPanelMode === 'intersection' && intersectionDrawingMode === 'marker'`.
 *
 *   - Attach a mousedown listener on `map` to start a preview-drag that
 *     creates a new intersection on mouseup.
 *   - Attach drag listeners to the active block's center/tip handles (from
 *     `overlaysRef`) so moving a handle updates `insertionPoint` or
 *     `azimuth`/`arrowLengthFeet` live.
 */
export function useIsdBlockDrawing({
  map,
  pathsPanelMode,
  pendingIsdDraft,
  onCancelPendingIsdDraft,
  overlaysRef,
  activeIntersectionId,
  onAddIntersection,
  onUpdateIntersection,
  onSetActiveIntersection,
}) {
  const activeIdRef = useRef(activeIntersectionId);
  activeIdRef.current = activeIntersectionId;

  const onAddRef = useRef(onAddIntersection);
  onAddRef.current = onAddIntersection;
  const onUpdateRef = useRef(onUpdateIntersection);
  onUpdateRef.current = onUpdateIntersection;
  const onSetActiveRef = useRef(onSetActiveIntersection);
  onSetActiveRef.current = onSetActiveIntersection;
  const onCancelDraftRef = useRef(onCancelPendingIsdDraft);
  onCancelDraftRef.current = onCancelPendingIsdDraft;
  const hasPendingDraftRef = useRef(!!pendingIsdDraft);
  hasPendingDraftRef.current = !!pendingIsdDraft;

  // ── Escape key → cancel pending placement, else deselect ──
  useEffect(() => {
    if (pathsPanelMode !== 'intersection') return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (hasPendingDraftRef.current) onCancelDraftRef.current?.();
      else onSetActiveRef.current?.(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pathsPanelMode]);

  // ── Click on empty map → deselect the active block ──
  // gmap's `click` event only fires when no clickable overlay catches the
  // event, so clicks on a block's outer circle (which selects it) won't
  // hit this. Skip while a placement draft is pending.
  useEffect(() => {
    if (!map || !window.google?.maps?.event) return;
    if (pathsPanelMode !== 'intersection') return;
    const g = window.google.maps;
    const listener = g.event.addListener(map, 'click', () => {
      if (hasPendingDraftRef.current) return;
      onSetActiveRef.current?.(null);
    });
    return () => g.event.removeListener(listener);
  }, [map, pathsPanelMode]);

  // ── Create-by-drag (active only while a draft is pending) ──
  useEffect(() => {
    if (!map || !window.google?.maps?.event) return;
    if (pathsPanelMode !== 'intersection' || !pendingIsdDraft) return;

    const g = window.google.maps;
    const mapDiv = map.getDiv();
    if (mapDiv) mapDiv.style.cursor = 'crosshair';

    let preview = null;
    let center = null;
    let mouseMoveListener = null;
    let mouseUpListener = null;
    let isCreating = false;

    const createPreview = (origin) => {
      const mainOpts = (radFt, fill) => ({
        map,
        center: origin,
        radius: radFt * METERS_PER_FOOT,
        strokeColor: COLOR_ACTIVE,
        strokeWeight: 2,
        fillColor: COLOR_ACTIVE,
        fillOpacity: fill,
        clickable: false,
        zIndex: 22,
      });
      const haloOpts = (radFt) => ({
        map,
        center: origin,
        radius: radFt * METERS_PER_FOOT,
        strokeColor: COLOR_ACTIVE_HALO,
        strokeWeight: 4,
        fillOpacity: 0,
        clickable: false,
        zIndex: 20,
      });
      return {
        innerHalo: new g.Circle(haloOpts(FT_INNER)),
        middleHalo: new g.Circle(haloOpts(FT_MIDDLE)),
        outerHalo: new g.Circle(haloOpts(FT_OUTER)),
        arrowHalo: new g.Polyline({
          map,
          path: [origin, origin],
          strokeColor: COLOR_ACTIVE_HALO,
          strokeWeight: 4,
          clickable: false,
          zIndex: 21,
        }),
        inner: new g.Circle(mainOpts(FT_INNER, 0.15)),
        middle: new g.Circle({ ...mainOpts(FT_MIDDLE, 0), fillOpacity: 0 }),
        outer: new g.Circle({ ...mainOpts(FT_OUTER, 0), fillOpacity: 0 }),
        arrow: new g.Polyline({
          map,
          path: [origin, origin],
          strokeColor: COLOR_ACTIVE,
          strokeWeight: 2,
          clickable: false,
          zIndex: 23,
          icons: [{
            icon: {
              path: g.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 3.5,
              strokeColor: COLOR_ACTIVE_HALO,
              strokeWeight: 1,
              fillColor: COLOR_ACTIVE,
              fillOpacity: 1,
            },
            offset: '100%',
          }],
        }),
      };
    };

    const destroyPreview = () => {
      if (!preview) return;
      // setVisible(false) BEFORE setMap(null): on some Maps builds setMap(null)
      // defers the actual removal to the next paint, leaving a "ghost" imprint
      // that nothing holds a reference to anymore (preview is nulled below).
      // Mirrors removeBundle() in useIsdOppositeBlockOverlays.
      [
        preview.inner,
        preview.middle,
        preview.outer,
        preview.arrow,
        preview.innerHalo,
        preview.middleHalo,
        preview.outerHalo,
        preview.arrowHalo,
      ].forEach((o) => {
        if (!o) return;
        try { o.setVisible?.(false); } catch (_) { /* not every overlay supports it */ }
        o.setMap(null);
      });
      preview = null;
    };

    // If the mousedown lands on an active-block handle marker, let the marker
    // own the event — don't start a new-block drag.
    const isOverActiveHandle = (latLng) => {
      const id = activeIdRef.current;
      if (!id) return false;
      const bundle = overlaysRef.current?.[id];
      if (!bundle) return false;
      const handles = [bundle.centerHandle, bundle.tipHandle];
      for (const h of handles) {
        if (!h.getVisible()) continue;
        const pos = h.getPosition();
        if (!pos) continue;
        const distFt = computeDistanceFeet(
          { lat: pos.lat(), lng: pos.lng() },
          { lat: latLng.lat(), lng: latLng.lng() },
        );
        if (distFt < 1.5) return true;
      }
      return false;
    };

    // If the mousedown lands inside any existing block, skip creation — the
    // circle's own click listener will handle selection if appropriate.
    const isOverAnyBlock = (latLng) => {
      for (const bundle of Object.values(overlaysRef.current ?? {})) {
        const c = bundle.innerCircle?.getCenter?.();
        if (!c) continue;
        const d = computeDistanceFeet(
          { lat: c.lat(), lng: c.lng() },
          { lat: latLng.lat(), lng: latLng.lng() },
        );
        if (d <= HIT_RADIUS_FT) return true;
      }
      return false;
    };

    const mouseDownListener = g.event.addListener(map, 'mousedown', (event) => {
      if (!event.latLng) return;
      if (isOverActiveHandle(event.latLng)) return;
      if (isOverAnyBlock(event.latLng)) return;

      // Idempotency guard: if a previous gesture is still in flight — a rapid
      // second mousedown, or a drag whose mouseup was released off the map so
      // gmap's map-level 'mouseup' never fired — tear it down before starting a
      // new one. Otherwise the old preview's overlays orphan onto the map (an
      // "imprinted" block): nothing references them anymore, so no state update
      // or delete-all can clear them — only a page refresh tears down the map.
      if (mouseMoveListener) g.event.removeListener(mouseMoveListener);
      if (mouseUpListener) g.event.removeListener(mouseUpListener);
      mouseMoveListener = null;
      mouseUpListener = null;
      destroyPreview();

      isCreating = true;
      center = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      preview = createPreview(center);

      map.setOptions({ draggable: false });

      mouseMoveListener = g.event.addListener(map, 'mousemove', (moveEvent) => {
        if (!moveEvent.latLng || !preview) return;
        const cursor = { lat: moveEvent.latLng.lat(), lng: moveEvent.latLng.lng() };
        preview.arrow.setPath([center, cursor]);
        preview.arrowHalo?.setPath([center, cursor]);
      });

      mouseUpListener = g.event.addListener(map, 'mouseup', (upEvent) => {
        if (!isCreating) return;
        isCreating = false;
        map.setOptions({ draggable: true });

        const cursor = upEvent.latLng
          ? { lat: upEvent.latLng.lat(), lng: upEvent.latLng.lng() }
          : center;
        const distFt = computeDistanceFeet(center, cursor);
        const azimuth = distFt >= MIN_DRAG_FT ? computeBearingDeg(center, cursor) : 0;
        const arrowLengthFeet = distFt >= MIN_DRAG_FT ? distFt : DEFAULT_ARROW_LENGTH_FT;

        destroyPreview();

        if (mouseMoveListener) g.event.removeListener(mouseMoveListener);
        if (mouseUpListener) g.event.removeListener(mouseUpListener);
        mouseMoveListener = null;
        mouseUpListener = null;

        onAddRef.current?.({
          insertionPoint: center,
          azimuth,
          arrowLengthFeet,
        });
      });
    });

    return () => {
      if (mapDiv) mapDiv.style.cursor = '';
      if (mouseDownListener) g.event.removeListener(mouseDownListener);
      if (mouseMoveListener) g.event.removeListener(mouseMoveListener);
      if (mouseUpListener) g.event.removeListener(mouseUpListener);
      map.setOptions({ draggable: true });
      destroyPreview();
    };
  }, [map, pathsPanelMode, pendingIsdDraft, overlaysRef]);

  // ── Handle-drag for the active block ──
  useEffect(() => {
    if (!map || !window.google?.maps?.event) return;
    if (pathsPanelMode !== 'intersection') return;
    const id = activeIntersectionId;
    if (!id) return;
    const bundle = overlaysRef.current?.[id];
    if (!bundle) return;

    const g = window.google.maps;
    const listeners = [];

    // Drag the inner disc itself to move the whole mark. The disc updates
    // its own center (gmap built-in); we manually move every other overlay
    // (other circles, halos, arrow) live so the block stays cohesive.
    const moveBundleVisuals = (newCenter) => {
      bundle.middleCircle.setCenter(newCenter);
      bundle.outerCircle.setCenter(newCenter);
      bundle.innerHalo?.setCenter(newCenter);
      bundle.middleHalo?.setCenter(newCenter);
      bundle.outerHalo?.setCenter(newCenter);
      const path = bundle.arrow.getPath();
      if (path && path.getLength() >= 2) {
        const tipPos = path.getAt(1);
        bundle.arrow.setPath([newCenter, tipPos]);
        bundle.arrowHalo?.setPath([newCenter, tipPos]);
      }
    };
    listeners.push(g.event.addListener(bundle.innerCircle, 'drag', () => {
      const c = bundle.innerCircle.getCenter();
      if (!c) return;
      moveBundleVisuals({ lat: c.lat(), lng: c.lng() });
    }));
    listeners.push(g.event.addListener(bundle.innerCircle, 'dragend', () => {
      const c = bundle.innerCircle.getCenter();
      if (!c) return;
      onUpdateRef.current?.(id, {
        insertionPoint: { lat: c.lat(), lng: c.lng() },
      });
    }));

    listeners.push(g.event.addListener(bundle.tipHandle, 'drag', (e) => {
      if (!e.latLng) return;
      const centerPos = bundle.innerCircle.getCenter();
      if (!centerPos) return;
      const centerLl = { lat: centerPos.lat(), lng: centerPos.lng() };
      const cursor = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      bundle.arrow.setPath([centerLl, cursor]);
    }));
    listeners.push(g.event.addListener(bundle.tipHandle, 'dragend', (e) => {
      if (!e.latLng) return;
      const centerPos = bundle.innerCircle.getCenter();
      if (!centerPos) return;
      const centerLl = { lat: centerPos.lat(), lng: centerPos.lng() };
      const cursor = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const azimuth = computeBearingDeg(centerLl, cursor);
      const arrowLengthFeet = Math.max(MIN_DRAG_FT, computeDistanceFeet(centerLl, cursor));
      onUpdateRef.current?.(id, { azimuth, arrowLengthFeet });
    }));

    bundle.listeners = (bundle.listeners || []).concat(listeners);

    return () => {
      listeners.forEach((l) => g.event.removeListener(l));
      if (bundle.listeners) {
        bundle.listeners = bundle.listeners.filter((l) => !listeners.includes(l));
      }
    };
  }, [map, pathsPanelMode, activeIntersectionId, overlaysRef]);
}
